import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail, saveToken } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import PasswordInput from "../PasswordInput";
import ConsentChecks from "../ConsentChecks";

export default function AcceptInvite() {
  const { token } = useParams();
  const { user, refresh } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [mode, setMode] = useState("register"); // "register" | "login"
  const [pwd, setPwd] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState({ terms: false, privacy: false, beta: false });
  const [legalVersions, setLegalVersions] = useState({ terms: "1.0", privacy: "1.0", beta: "1.0" });
  const allConsented = consent.terms && consent.privacy && consent.beta;

  useEffect(() => {
    api.get(`/team/invite/${token}`)
      .then((r) => {
        const next = r.data?.lang === "fr" ? "fr" : "en";
        setLang(next);
        setInvite(r.data);
      })
      .catch(() => setErr(t("team.acceptInvalid")));
  }, [token, t, setLang]);

  const tpl = (key, vars) => {
    let s = t(key);
    Object.entries(vars || {}).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
    return s;
  };

  const acceptLoggedIn = async () => {
    setBusy(true); setErr("");
    try {
      await api.post(`/team/invite/${token}/accept`);
      setOk(true);
      if (refresh) await refresh();
      setTimeout(() => navigate("/app"), 900);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const submitInline = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { password: pwd, lang };
      if (mode === "register") {
        if (!allConsented) {
          setErr(t("auth.consentRequired"));
          setBusy(false);
          return;
        }
        payload.name = name.trim();
        payload.consent_terms = consent.terms;
        payload.consent_privacy = consent.privacy;
        payload.consent_beta = consent.beta;
        payload.consent_versions = legalVersions;
      }
      const r = await api.post(`/team/invite/${token}/accept-inline`, payload);
      if (r.data.token) saveToken(r.data.token);
      setOk(true);
      if (refresh) await refresh();
      setTimeout(() => navigate("/app"), 900);
    } catch (e2) {
      const detail = formatApiErrorDetail(e2.response?.data?.detail) || e2.message;
      // If we tried login but no account exists, switch to register
      if (mode === "login" && e2.response?.status === 401) {
        setErr(t("team.wrongPassword"));
      } else if (mode === "login" && e2.response?.status === 400 && /name required/i.test(detail || "")) {
        setMode("register");
        setErr("");
      } else {
        setErr(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  if (user === null) return <div className="tb-app"><div className="tb-loading">{t("common.loading")}</div></div>;

  const inviteEmailLower = (invite?.email || "").toLowerCase();
  const userEmailLower = (user?.email || "").toLowerCase();
  const wrongEmail = user && userEmailLower && inviteEmailLower && userEmailLower !== inviteEmailLower;
  const matchesUser = user && userEmailLower && userEmailLower === inviteEmailLower;

  return (
    <div className="tb-app">
      <header className="tb-app-bar">
        <Link to="/" className="tb-app-logo">Traction<span>Board</span></Link>
      </header>
      <main>
        <div className="tb-page" style={{ maxWidth: 560 }}>
          <h1 className="tb-h1" data-testid="accept-invite-heading">{t("team.acceptTitle")}</h1>

          {err && <div className="tb-form-error" data-testid="accept-error" style={{ marginBottom: 12 }}>{err}</div>}

          {!err && !invite && <div className="tb-loading">{t("common.loading")}</div>}

          {invite && (
            <div className="tb-block">
              <p style={{ marginTop: 0 }} data-testid="accept-intro">
                {tpl("team.acceptIntro", {
                  inviter: invite.inviter_name || "A founder",
                  company: invite.company || "their company",
                })}
              </p>
              <p className="tb-muted" style={{ fontSize: "0.85rem", marginTop: 4 }}>
                {t("team.invitedAs")} <strong>{invite.email}</strong>
              </p>

              {ok && (
                <div className="tb-badge good" data-testid="accept-success" style={{ marginTop: 10 }}>
                  ✓ {t("team.acceptSuccess")}
                </div>
              )}

              {/* Case 1 — already logged in with matching email */}
              {!ok && matchesUser && (
                <button
                  className="tb-primary-btn"
                  onClick={acceptLoggedIn}
                  disabled={busy}
                  data-testid="accept-cta"
                  style={{ marginTop: 14 }}
                >
                  {busy ? t("team.acceptBusy") : t("team.acceptCta")}
                </button>
              )}

              {/* Case 2 — logged in with the WRONG email */}
              {!ok && wrongEmail && (
                <p className="tb-form-error" data-testid="accept-wrong-email" style={{ marginTop: 14 }}>
                  {tpl("team.acceptWrongEmail", { email: invite.email })}
                </p>
              )}

              {/* Case 3 — logged OUT: inline login or register */}
              {!ok && !user && (
                <form onSubmit={submitInline} className="tb-form" data-testid="accept-inline-form" style={{ marginTop: 16 }}>
                  <div className="tb-lang-pick" style={{ alignSelf: "flex-start" }}>
                    <button type="button" className={mode === "register" ? "on" : ""} onClick={() => { setMode("register"); setErr(""); }} data-testid="mode-register">
                      {t("team.iAmNew")}
                    </button>
                    <button type="button" className={mode === "login" ? "on" : ""} onClick={() => { setMode("login"); setErr(""); }} data-testid="mode-login">
                      {t("team.iHaveAccount")}
                    </button>
                  </div>

                  <div>
                    <label>{t("auth.email")}</label>
                    <input type="email" value={invite.email} disabled data-testid="inline-email" />
                  </div>

                  {mode === "register" && (
                    <div>
                      <label>{t("auth.name")}</label>
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t("auth.name")}
                        data-testid="inline-name"
                      />
                    </div>
                  )}

                  <div>
                    <label>{mode === "login" ? t("auth.password") : t("auth.newPassword")}</label>
                    <PasswordInput
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      required
                      minLength={6}
                      testid="inline-password"
                      autoComplete={mode === "login" ? "current-password" : "new-password"}
                    />
                  </div>

                  {mode === "register" && (
                    <ConsentChecks
                      consent={consent}
                      setConsent={setConsent}
                      onVersions={setLegalVersions}
                      testPrefix="invite-consent"
                    />
                  )}

                  <button type="submit" className="tb-primary-btn" disabled={busy || (mode === "register" && !allConsented)} data-testid="accept-inline-submit">
                    {busy
                      ? t("team.acceptBusy")
                      : mode === "login" ? t("team.loginAndJoin") : t("team.createAndJoin")}
                  </button>

                  <p className="tb-muted" style={{ fontSize: "0.78rem", textAlign: "center", marginTop: 0 }}>
                    {mode === "login" ? (
                      <>
                        {t("team.noAccountYet")} <button type="button" className="tb-link-btn" onClick={() => { setMode("register"); setErr(""); }} data-testid="switch-to-register">{t("team.iAmNew")}</button>
                      </>
                    ) : (
                      <>
                        {t("team.haveAccount")} <button type="button" className="tb-link-btn" onClick={() => { setMode("login"); setErr(""); }} data-testid="switch-to-login">{t("team.iHaveAccount")}</button>
                      </>
                    )}
                  </p>
                </form>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
